/* =============================================================================
   EventBinder - auto-tracked listeners, timers and observers.
   Every DOM listener, timeout, interval and observer a component creates goes
   through its binder, so Component.destroy() can ALWAYS release everything:
   teardown is leak-free by construction rather than by discipline.
   RULE: anything registered outside the binder is invisible to destroy() and
   leaks - a raw addEventListener or setInterval in a component is a defect.
   There is no per-listener release: a listener lives until removeAll(), so bind
   long-lived handlers once per component, never once per user action.
   API: add · delegate · timeout · interval · clearTimer · observe · removeAll.
   No dependencies.
   ============================================================================= */

export class EventBinder {
  /** @param {HTMLElement} [owner] the owning element (for debugging only) */
  constructor(owner) {
    this.owner = owner || null;
    /** @type {{t:EventTarget,type:string,fn:EventListener,opts:any}[]} */
    this._listeners = [];
    this._timeouts = new Set();
    this._intervals = new Set();
    this._observers = new Set();
  }

  /** addEventListener + track. Returns fn so callers can keep a reference. */
  add(target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this._listeners.push({ t: target, type, fn, opts });
    return fn;
  }

  /** Delegated listener: fires fn(e, match) when e.target matches selector inside target. */
  delegate(target, type, selector, fn, opts) {
    const wrapped = (e) => {
      const match = e.target && e.target.closest ? e.target.closest(selector) : null;
      if (match && target.contains(match)) fn(e, match);
    };
    return this.add(target, type, wrapped, opts);
  }

  /** Tracked setTimeout - auto-cleared on removeAll/destroy. */
  timeout(fn, ms) {
    const id = setTimeout(() => { this._timeouts.delete(id); fn(); }, ms);
    this._timeouts.add(id);
    return id;
  }

  /** Tracked setInterval - auto-cleared on removeAll/destroy, so it cannot outlive the component. */
  interval(fn, ms) {
    const id = setInterval(fn, ms);
    this._intervals.add(id);
    return id;
  }

  clearTimer(id) {
    if (this._timeouts.delete(id)) clearTimeout(id);
    if (this._intervals.delete(id)) clearInterval(id);
  }

  /** Track a MutationObserver/ResizeObserver/IntersectionObserver for teardown. */
  observe(observer) {
    this._observers.add(observer);
    return observer;
  }

  /** Release EVERYTHING: listeners, timers and observers. Idempotent. */
  removeAll() {
    for (const { t, type, fn, opts } of this._listeners) t.removeEventListener(type, fn, opts);
    this._listeners.length = 0;
    for (const id of this._timeouts) clearTimeout(id);
    this._timeouts.clear();
    for (const id of this._intervals) clearInterval(id);
    this._intervals.clear();
    for (const o of this._observers) { try { o.disconnect(); } catch { /* already gone */ } }
    this._observers.clear();
  }
}
